import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import MultiSelect from '../../../../../../components/global/MultiSelect';
import Input from '../../../../../../components/global/Input';
import Button from '../../../../../../components/global/Button';
import Label from '../../../../../../components/global/Label';
import useAuthenticated from '../../../../../../hooks/useAuthenticated';
import {
  MeForAlbumFoldersSelect,
  MeForRosterIdentityListsSelect,
} from '../../../../../../graphql/queries/account';
import { AlbumFolderForAlbumSelect } from '../../../../../../graphql/queries/album-folder';
import ToggleSwitch from '../../../../../../components/global/ToggleSwitch';
import {
  ADDRESS_BOOK_CONTACT_TYPE_FILTER,
  ADDRESS_BOOK_CONTACT_TYPE_FILTER_LABEL,
} from '../../constants';
import useBasename from '../../../../../../components/routing/useBasename';
import { getBasenameDefinitions } from '../../../../../../util/role-definitions';
import { canAccessGroupAndCabinOnBasename } from '../../../../../../util/product-permissions';
import { getAlbumFolderAlbumFlatList } from '../../../../../../util/album-folder';

const propTypes = {
  contactFilter: PropTypes.oneOf(
    Object.values(ADDRESS_BOOK_CONTACT_TYPE_FILTER),
  ),
  setContactFilter: PropTypes.func,
  albumFolderId: PropTypes.string,
  setAlbumFolderId: PropTypes.func,
  albums: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  setAlbums: PropTypes.func,
  rosterIdentityLists: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  setRosterIdentityLists: PropTypes.func,
  cabinName: PropTypes.string,
  groupName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};

const AddressBookFilters = ({
  contactFilter,
  setContactFilter,
  albumFolderId,
  setAlbumFolderId,
  albums,
  setAlbums,
  rosterIdentityLists,
  setRosterIdentityLists,
  cabinName,
  groupName,
  onConfirm,
  onRequestClose,
}) => {
  const basename = useBasename();
  const authenticated = useAuthenticated();
  const definitions = getBasenameDefinitions(basename);
  const canAccessCabinAndGroup = canAccessGroupAndCabinOnBasename(basename);

  const [localContactFilter, setLocalContactFilter] = useState(
    contactFilter || ADDRESS_BOOK_CONTACT_TYPE_FILTER.CONTACTS_ONLY,
  );
  const [localAlbumFolderId, setLocalAlbumFolderId] = useState(
    albumFolderId || '',
  );
  const [localAlbums, setLocalAlbums] = useState(albums || []);
  const [localRosterIdentityLists, setLocalRosterIdentityLists] = useState(
    rosterIdentityLists || [],
  );
  const [localCabinName, setLocalCabinName] = useState(cabinName || '');
  const [localGroupName, setLocalGroupName] = useState(groupName || '');

  const { data: { me } = {} } = useQuery(MeForAlbumFoldersSelect, {
    variables: {
      album_type_subtype_web_app_basename_filters:
        definitions.allowedContentTypes,
    },
    skip: !authenticated,
  });
  const { data: { me: listsUser } = {} } = useQuery(
    MeForRosterIdentityListsSelect,
    {
      skip: !authenticated,
    },
  );

  const rosterIdentityListOptions = (
    listsUser?.roster_identity_lists?.records ?? []
  ).map(list => ({
    label: list.name,
    value: list.id,
  }));
  const { data: { albumFolder } = {} } = useQuery(AlbumFolderForAlbumSelect, {
    variables: {
      id: localAlbumFolderId,
    },
    skip: !localAlbumFolderId,
  });

  const albumOptions = useMemo(() => {
    if (!albumFolder) {
      return [];
    }

    return getAlbumFolderAlbumFlatList(albumFolder, true);
  }, [albumFolder]);

  const handleConfirm = () => {
    if (setContactFilter) setContactFilter(localContactFilter);
    if (setAlbumFolderId) setAlbumFolderId(localAlbumFolderId);
    if (setAlbums) setAlbums(localAlbums);
    if (setRosterIdentityLists)
      setRosterIdentityLists(localRosterIdentityLists);

    onConfirm({
      contactFilter: localContactFilter,
      albumFolderId: localAlbumFolderId,
      albums: localAlbums,
      cabinName: localCabinName,
      groupName: localGroupName,
    });

    onRequestClose();
  };

  return (
    <div className="address-book-filters-modal">
      <div className="modal-content-header">
        <h2>Filter your address book</h2>
      </div>
      <div className="modal-content-body">
        <MultiSelect
          id="album-folder-select"
          name="albumFolderSelect"
          label="Filter by gallery"
          data-testid="album-folder-select"
          placeholder="Select a gallery"
          value={
            albumFolder
              ? { value: albumFolder.id, label: albumFolder.name }
              : null
          }
          options={(me?.album_folders?.records ?? []).map(folder => ({
            label: folder.name,
            value: folder.id,
          }))}
          onChange={option => {
            setLocalAlbumFolderId(option?.value || '');
            setLocalAlbums([]);
            if (!option?.value) {
              setLocalCabinName('');
              setLocalGroupName('');
              setLocalContactFilter(
                ADDRESS_BOOK_CONTACT_TYPE_FILTER.CONTACTS_ONLY,
              );
            }
          }}
          isClearable
        />

        <MultiSelect
          id="roster-identity-lists-select"
          name="rosterIdentityListsSelect"
          label="Filter by list"
          data-testid="roster-identity-lists-select"
          placeholder="All lists"
          value={localRosterIdentityLists[0] || null}
          options={rosterIdentityListOptions}
          onChange={option =>
            setLocalRosterIdentityLists(option ? [option] : [])
          }
          isClearable
          maxMenuHeight={200}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
          }}
        />

        {localAlbumFolderId && (
          <Fragment>
            <MultiSelect
              id="albums-select"
              name="albumsSelect"
              label="Filter by albums (optional)"
              data-testid="albums-select"
              placeholder={
                !albumFolder ? 'Select a gallery first...' : 'Select albums'
              }
              noOptionsMessage={() =>
                !albumFolder
                  ? 'Select a gallery first...'
                  : 'This gallery does not have any albums'
              }
              isDisabled={!albumFolder}
              value={localAlbums}
              options={albumOptions.map(album => ({
                label: album.name,
                value: album.id,
              }))}
              onChange={options => setLocalAlbums(options || [])}
              isMulti
              isClearable
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
              }}
            />

            {canAccessCabinAndGroup && (
              <Fragment>
                <div className="form-group">
                  <Label id="cabin-name-input" label="Filter by cabin" />
                  <Input
                    id="cabin-name-input"
                    name="cabinName"
                    type="text"
                    placeholder="Select..."
                    value={localCabinName}
                    onChange={event => {
                      setLocalCabinName(event.target.value);
                    }}
                  />
                </div>

                <div className="form-group">
                  <Label id="group-name-input" label="Filter by group name" />
                  <Input
                    id="group-name-input"
                    name="groupName"
                    type="text"
                    placeholder="Select..."
                    value={localGroupName}
                    onChange={event => {
                      setLocalGroupName(event.target.value);
                    }}
                  />
                </div>
              </Fragment>
            )}
          </Fragment>
        )}

        {localAlbumFolderId && (
          <ToggleSwitch
            id="contact-filter-toggle"
            name="contactFilterToggle"
            label="Filter by relationship type"
            labelTip="You can filter by either Users joined or Contacts linked to a Gallery or Album."
            offLabel={
              ADDRESS_BOOK_CONTACT_TYPE_FILTER_LABEL[
                ADDRESS_BOOK_CONTACT_TYPE_FILTER.CONTACTS_ONLY
              ]
            }
            onLabel={
              ADDRESS_BOOK_CONTACT_TYPE_FILTER_LABEL[
                ADDRESS_BOOK_CONTACT_TYPE_FILTER.JOINED_ONLY
              ]
            }
            checked={
              localContactFilter ===
              ADDRESS_BOOK_CONTACT_TYPE_FILTER.JOINED_ONLY
            }
            onChange={checked =>
              setLocalContactFilter(
                checked
                  ? ADDRESS_BOOK_CONTACT_TYPE_FILTER.JOINED_ONLY
                  : ADDRESS_BOOK_CONTACT_TYPE_FILTER.CONTACTS_ONLY,
              )
            }
          />
        )}
      </div>
      <div className="modal-content-footer">
        <Button btnStyle="primary" onClick={handleConfirm}>
          Confirm
        </Button>
      </div>
    </div>
  );
};

AddressBookFilters.propTypes = propTypes;

export default AddressBookFilters;
